import React, { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

function formatScore(value) {
  return Number(value).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
}

function getPriorityColor(value) {
  if (value >= 66) return 'error';
  if (value >= 33) return 'warning';
  return 'success';
}

function RecommendationMetric({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{value}</Typography>
    </Box>
  );
}

export default function SelfReflectionTrainingRecommendations({ recommendations = [], showMetrics = true }) {
  const [showTiedRecommendations, setShowTiedRecommendations] = useState(false);

  useEffect(() => {
    setShowTiedRecommendations(false);
  }, [recommendations]);

  const primaryRecommendations = recommendations.slice(0, 3);
  const thirdRecommendation = primaryRecommendations[2];
  const tiedRecommendations = thirdRecommendation
    ? recommendations.slice(3).filter((recommendation) => recommendation.rank === thirdRecommendation.rank)
    : [];
  const visibleRecommendations = showTiedRecommendations
    ? [...primaryRecommendations, ...tiedRecommendations]
    : primaryRecommendations;

  return (
    <Stack component="section" spacing={1.5} aria-label="Priorisierte Trainingsthemen">
      {showMetrics && <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'info.main',
          borderRadius: '8px !important',
          overflow: 'hidden',
          bgcolor: 'rgba(2, 136, 209, 0.08)',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon color="info" />}>
          <Typography variant="subtitle2" color="info.dark" sx={{ fontWeight: 800 }}>Wie entsteht die Trainingsempfehlung?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            <Typography variant="body2">Eine Selbst- oder Lehrhöhenretter-Einschätzung genügt. Sind beide vorhanden, werden sie gleichgewichtet verrechnet; andernfalls zählt die vorhandene Einschätzung vollständig.</Typography>
            <Typography variant="body2">Der Übungsbedarf setzt sich ausgewogen aus dem Defizit der gesamten Gruppe (50 %), dem Defizit des schwächsten Viertels (30 %) und dem Anteil der Werte bis einschließlich 2,5 (20 %) zusammen.</Typography>
            <Typography variant="body2">Fehlende Werte werden nicht als Schwäche gewertet. Die Datenbasis zeigt deshalb, wie viele der ausgewählten und für das Kriterium relevanten Teilnehmer berücksichtigt wurden.</Typography>
            <Typography variant="body2">Vorschläge mit identischen Kennzahlen erhalten denselben Rang. Weitere Gleichstände am dritten sichtbaren Platz können aufgeklappt werden.</Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>}

      {!recommendations.length && <Alert severity="info">Für den ausgewählten Teilnehmerkreis liegen noch keine verwertbaren Einschätzungen vor.</Alert>}
      {recommendations.length > 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Stack divider={<Divider flexItem />}>
            {visibleRecommendations.map((recommendation, index) => {
              const controlsTiedRecommendations = index === 2 && tiedRecommendations.length > 0;
              const showsTiedSummary = controlsTiedRecommendations && !showTiedRecommendations;
              return (
              <Box
                key={recommendation.criterionKey}
                data-recommendation-rank={recommendation.rank ?? index + 1}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: showMetrics
                    ? { xs: '36px minmax(0, 1fr)', md: '40px minmax(220px, 1fr) minmax(360px, 1.4fr) 120px' }
                    : '40px minmax(0, 1fr)',
                  gap: { xs: 1, md: 2 },
                  alignItems: 'center',
                  p: { xs: 1.5, sm: 2 },
                }}
              >
                <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', fontWeight: 800 }}>{recommendation.rank ?? index + 1}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {showsTiedSummary ? 'Mehrere Themenempfehlungen' : recommendation.criterionLabel}
                  </Typography>
                  {showsTiedSummary ? (
                    <Button size="small" endIcon={<ExpandMoreIcon />} onClick={() => setShowTiedRecommendations(true)} sx={{ mt: 0.5, px: 0 }}>
                      Themen aufklappen
                    </Button>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        {[recommendation.categoryLabel, recommendation.groupLabel].filter(Boolean).join(' · ')}
                      </Typography>
                      {showMetrics && <LinearProgress color={getPriorityColor(recommendation.priorityScore)} variant="determinate" value={recommendation.priorityScore} sx={{ mt: 1, height: 6, borderRadius: 3 }} />}
                    </>
                  )}
                </Box>
                {showMetrics && <Box sx={{ gridColumn: { xs: '1 / -1', md: 3 }, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
                  <RecommendationMetric label="Gruppenmittel" value={formatScore(recommendation.groupAverage)} />
                  <RecommendationMetric label="Schwächstes Viertel" value={formatScore(recommendation.weakestQuarterAverage)} />
                  <RecommendationMetric label="Förderbedarf" value={`${recommendation.affectedCount}/${recommendation.evaluatedCount}`} />
                  <RecommendationMetric label="Datenbasis" value={`${recommendation.evaluatedCount}/${recommendation.applicableCount}`} />
                </Box>}
                {showMetrics && <Stack spacing={0.5} alignItems={{ xs: 'flex-start', md: 'flex-end' }} sx={{ gridColumn: { xs: '1 / -1', md: 4 } }}>
                  <Chip
                    color={getPriorityColor(recommendation.priorityScore)}
                    label={`${formatScore(recommendation.priorityScore)} % Bedarf`}
                    sx={{ fontWeight: 700 }}
                  />
                  {controlsTiedRecommendations && showTiedRecommendations && (
                    <Button
                      size="small"
                      endIcon={<ExpandLessIcon />}
                      onClick={() => setShowTiedRecommendations(false)}
                    >
                      Themen einklappen
                    </Button>
                  )}
                </Stack>}
              </Box>
              );
            })}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}